/**
 * QR Codes Manage — list, edit, delete, download.
 * Screen code: QR-003
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Search, Download, Pencil, Trash2, Copy, ExternalLink,
  QrCode, PlusCircle, Save, X,
} from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const API = process.env.REACT_APP_BACKEND_URL;

export default function QRCodesManage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [editing, setEditing] = useState(null); // full doc being edited
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/admin/qr-codes`, { headers: getAuthHeaders() });
      setItems(res.data || []);
    } catch (err) {
      toast.error(t('فشل التحميل', 'Load failed') + ': ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = items.filter((q) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (q.label || '').toLowerCase().includes(s) ||
      (q.short_code || '').toLowerCase().includes(s) ||
      (q.platform || '').toLowerCase().includes(s) ||
      (q.target_url || '').toLowerCase().includes(s)
    );
  });

  const copyShortUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('تم النسخ', 'Copied'));
    } catch {
      toast.error(t('فشل النسخ', 'Copy failed'));
    }
  };

  const downloadImage = async (qr) => {
    try {
      const res = await axios.get(
        `${API}/api/admin/qr-codes/${qr.id}/image`,
        { headers: getAuthHeaders(), responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'image/png' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(qr.label || 'qr').replace(/\s+/g, '_')}_${qr.short_code}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(t('فشل التنزيل', 'Download failed'));
    }
  };

  const doDelete = async (qr) => {
    try {
      await axios.delete(`${API}/api/admin/qr-codes/${qr.id}`, { headers: getAuthHeaders() });
      toast.success(t('تم الحذف', 'Deleted'));
      setItems((xs) => xs.filter((x) => x.id !== qr.id));
    } catch (err) {
      toast.error(t('فشل الحذف', 'Delete failed'));
    } finally {
      setDeleting(null);
    }
  };

  const breadcrumb = [
    { labelAr: 'لوحة التحكم',   labelEn: 'Dashboard',       path: '/dashboard'         },
    { labelAr: 'نظام QR Code',  labelEn: 'QR Code System',  path: '/qr-codes'          },
    { labelAr: 'المتابعة',      labelEn: 'Manage'                                       },
  ];

  return (
    <DashboardLayout
      title="متابعة QR Codes"
      titleEn="Manage QR Codes"
      subtitle="كل الرموز التي أنشأتها — عدّل، حمّل، أو احذف."
      subtitleEn="Every code you created — edit, download or delete."
      screenCode="QR-003"
      backPath="/qr-codes"
      onBack={() => navigate('/qr-codes')}
      breadcrumb={breadcrumb}
    >
      <section
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6"
        dir={isAr ? 'rtl' : 'ltr'}
        data-testid="qr-manage-root"
      >
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-white/40" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('ابحث بالاسم / الرمز / المنصة / الرابط…', 'Search by label / code / platform / URL…')}
              className="ps-9 bg-white/[0.03] border-white/10 text-white"
              data-testid="qr-search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-white/50">
              {t(`الإجمالي: ${items.length}`, `Total: ${items.length}`)}
            </span>
            <Button
              onClick={() => navigate('/qr-codes/new')}
              className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-black font-bold gap-2"
              data-testid="qr-new-btn"
            >
              <PlusCircle className="w-4 h-4" />
              {t('جديد', 'New')}
            </Button>
          </div>
        </div>

        {/* Table / empty state */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-white/40 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            onNew={() => navigate('/qr-codes/new')}
            hasSearch={!!search}
            t={t}
          />
        ) : (
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-white">
                <thead className="bg-white/[0.03] text-white/60 text-xs uppercase">
                  <tr>
                    <th className="text-start px-4 py-3 font-bold">{t('الاسم', 'Label')}</th>
                    <th className="text-start px-4 py-3 font-bold">{t('المنصة / الصفحة', 'Platform / Page')}</th>
                    <th className="text-start px-4 py-3 font-bold">{t('الرابط', 'URL')}</th>
                    <th className="text-center px-4 py-3 font-bold">{t('المسحات', 'Scans')}</th>
                    <th className="text-end px-4 py-3 font-bold">{t('الإجراءات', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((q) => (
                    <tr key={q.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`qr-row-${q.short_code}`}>
                      <td className="px-4 py-3">
                        <div className="font-bold text-white">{q.label}</div>
                        <div className="text-[10px] text-[#d4af37] font-mono mt-0.5">{q.short_code}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-white/80 text-xs">{q.platform}</div>
                        {q.page && <div className="text-white/40 text-[10px] mt-0.5">{q.page}</div>}
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        <a
                          href={q.target_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-400 hover:underline truncate block"
                        >
                          {q.target_url}
                        </a>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full bg-[#d4af37]/15 text-[#d4af37] font-bold text-xs">
                          {q.scan_count || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <IconBtn
                            title={t('نسخ رابط QR', 'Copy QR URL')}
                            onClick={() => copyShortUrl(q.short_url)}
                            testid={`qr-copy-${q.short_code}`}
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </IconBtn>
                          <IconBtn
                            title={t('فتح الرابط', 'Open URL')}
                            onClick={() => window.open(q.target_url, '_blank')}
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </IconBtn>
                          <IconBtn
                            title={t('تنزيل PNG', 'Download PNG')}
                            onClick={() => downloadImage(q)}
                            testid={`qr-download-${q.short_code}`}
                          >
                            <Download className="w-3.5 h-3.5" />
                          </IconBtn>
                          <IconBtn
                            title={t('تعديل', 'Edit')}
                            onClick={() => setEditing(q)}
                            testid={`qr-edit-${q.short_code}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </IconBtn>
                          <IconBtn
                            title={t('حذف', 'Delete')}
                            onClick={() => setDeleting(q)}
                            testid={`qr-delete-${q.short_code}`}
                            danger
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </IconBtn>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Edit dialog */}
      <AnimatePresence>
        {editing && (
          <EditDialog
            qr={editing}
            onClose={() => setEditing(null)}
            onSaved={(updated) => {
              setItems((xs) => xs.map((x) => (x.id === updated.id ? updated : x)));
              setEditing(null);
            }}
            t={t}
            isAr={isAr}
          />
        )}
      </AnimatePresence>

      {/* Delete confirm */}
      <AnimatePresence>
        {deleting && (
          <ConfirmDialog
            title={t('تأكيد الحذف', 'Confirm delete')}
            body={t(
              `سيتم حذف الرمز "${deleting.label}" (${deleting.short_code}) وكل إحصائياته. هذا الإجراء لا يمكن التراجع عنه.`,
              `The code "${deleting.label}" (${deleting.short_code}) and all its stats will be deleted. This action cannot be undone.`,
            )}
            confirmLabel={t('احذف', 'Delete')}
            onConfirm={() => doDelete(deleting)}
            onCancel={() => setDeleting(null)}
          />
        )}
      </AnimatePresence>
    </DashboardLayout>
  );
}

// ─── Sub-components ─────────────────────────────────────────

function IconBtn({ onClick, title, children, danger, testid }) {
  return (
    <button
      title={title}
      onClick={onClick}
      data-testid={testid}
      className={`p-2 rounded-md border transition-colors ${
        danger
          ? 'border-red-500/20 text-red-400 hover:bg-red-500/10'
          : 'border-white/10 text-white/70 hover:bg-white/5 hover:text-white'
      }`}
    >
      {children}
    </button>
  );
}

function EmptyState({ onNew, hasSearch, t }) {
  return (
    <div className="text-center py-20 bg-white/[0.02] border border-white/10 rounded-2xl">
      <QrCode className="w-16 h-16 text-white/20 mx-auto mb-4" />
      <h3 className="text-white text-lg font-bold mb-2">
        {hasSearch ? t('لا توجد نتائج', 'No results') : t('لم تُنشئ أي رمز بعد', 'No QR codes yet')}
      </h3>
      <p className="text-white/50 text-sm mb-5">
        {hasSearch
          ? t('جرب كلمة بحث أخرى', 'Try a different search term')
          : t('ابدأ بإنشاء أول رمز QR الآن', 'Start by creating your first QR code')}
      </p>
      {!hasSearch && (
        <Button
          onClick={onNew}
          className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-black font-bold gap-2"
        >
          <PlusCircle className="w-4 h-4" />
          {t('إنشاء الآن', 'Create now')}
        </Button>
      )}
    </div>
  );
}

function EditDialog({ qr, onClose, onSaved, t, isAr }) {
  const [label, setLabel]         = useState(qr.label);
  const [platform, setPlatform]   = useState(qr.platform);
  const [page, setPage]           = useState(qr.page || '');
  const [targetUrl, setTargetUrl] = useState(qr.target_url);
  const [saving, setSaving]       = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/api/admin/qr-codes/${qr.id}`,
        { label, platform, page, target_url: targetUrl },
        { headers: getAuthHeaders() },
      );
      toast.success(t('✓ تم التحديث', '✓ Updated'));
      onSaved(res.data);
    } catch (err) {
      toast.error(t('فشل التحديث', 'Update failed') + ': ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        dir={isAr ? 'rtl' : 'ltr'}
        className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 max-w-lg w-full"
        data-testid="qr-edit-dialog"
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Pencil className="w-5 h-5 text-[#d4af37]" />
            <h2 className="text-white font-bold text-lg">{t('تعديل الرمز', 'Edit QR')}</h2>
          </div>
          <button onClick={onClose} className="p-1 text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-white/70 text-xs mb-1.5">{t('الاسم', 'Label')}</label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="bg-[#050505] border-white/10 text-white"
              data-testid="qr-edit-label"
            />
          </div>
          <div>
            <label className="block text-white/70 text-xs mb-1.5">{t('المنصة', 'Platform')}</label>
            <Input
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="bg-[#050505] border-white/10 text-white"
            />
          </div>
          <div>
            <label className="block text-white/70 text-xs mb-1.5">{t('الصفحة', 'Page')}</label>
            <Input
              value={page}
              onChange={(e) => setPage(e.target.value)}
              className="bg-[#050505] border-white/10 text-white"
            />
          </div>
          <div>
            <label className="block text-white/70 text-xs mb-1.5">{t('الرابط', 'Target URL')}</label>
            <Input
              value={targetUrl}
              onChange={(e) => setTargetUrl(e.target.value)}
              className="bg-[#050505] border-white/10 text-white font-mono text-xs"
              data-testid="qr-edit-url"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose} className="border-white/10 text-white/70">
            {t('إلغاء', 'Cancel')}
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-black font-bold gap-2"
            data-testid="qr-edit-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {t('حفظ', 'Save')}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function ConfirmDialog({ title, body, confirmLabel, onConfirm, onCancel }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0a0a0a] border border-red-500/20 rounded-2xl p-6 max-w-md w-full"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center">
            <Trash2 className="w-5 h-5 text-red-400" />
          </div>
          <h2 className="text-white font-bold text-lg">{title}</h2>
        </div>
        <p className="text-white/70 text-sm mb-6 leading-relaxed">{body}</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} className="border-white/10 text-white/70">
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            className="bg-red-500 hover:bg-red-600 text-white font-bold gap-2"
            data-testid="qr-delete-confirm"
          >
            <Trash2 className="w-4 h-4" />
            {confirmLabel}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}
